<template>
  <div style="overflow-x:hidden;">

    <!-- ═══ PAGE HERO ═══ -->
    <div class="nf-project-hero" :style="{ backgroundImage: 'linear-gradient(135deg,rgba(14,69,48,0.92) 0%,rgba(44,62,80,0.88) 100%), url(/img/Consultation1.jpg)' }">
      <div class="nf-ph-content">
        <span class="nf-ph-label">Our Work</span>
        <h1 class="nf-ph-title">Project Portfolio</h1>
        <p class="nf-ph-sub">
          Real-world impact across Kenya — from Environmental Impact Assessments to
          WASH programmes, climate adaptation, and community resilience.
        </p>
        <ul class="nf-breadcrumb mt-3">
          <li><router-link to="/">Home</router-link></li>
          <li class="sep"><i class="fas fa-chevron-right" style="font-size:.65rem;"></i></li>
          <li class="active">Projects</li>
        </ul>
      </div>
    </div>

    <!-- ═══ STATS STRIP ═══ -->
    <div class="nf-proj-stats-strip">
      <div class="container">
        <div class="row g-0 text-center">
          <div class="col-6 col-md-3" v-for="(s, i) in statsStrip" :key="i">
            <div class="nf-ps-item" :class="{'border-end': i < 3}">
              <div class="nf-ps-num">{{ s.value }}</div>
              <div class="nf-ps-label">{{ s.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══ FEATURED PROJECTS ═══ -->
    <section v-if="featured.length" class="py-6 px-3 px-lg-5 bg-white">
      <div class="container-fluid">
        <div class="d-flex align-items-end justify-content-between mb-5">
          <div>
            <span class="section-label">Highlighted Work</span>
            <h2 class="section-title mb-0">Featured Projects</h2>
          </div>
          <hr class="section-divider flex-shrink-0 ms-4 mb-2" style="width:80px;" />
        </div>
        <div class="row g-4">
          <div class="col-12 col-lg-6" v-for="proj in featured.slice(0,2)" :key="'f'+proj.id">
            <div class="nf-featured-card" @click="openModal(proj)">
              <div class="nf-fc-image">
                <img :src="proj.image_url || fallbackImg" :alt="proj.title" />
                <div class="nf-fc-overlay"></div>
                <span class="nf-fc-status" :class="statusClass(proj.status)">
                  <i class="fas fa-circle me-1" style="font-size:.5rem;vertical-align:middle;"></i>
                  {{ proj.status_label }}
                </span>
                <div v-if="proj.category" class="nf-fc-cat" :style="{background: proj.category.color}">
                  <i :class="proj.category.icon_class + ' me-1'"></i>{{ proj.category.name }}
                </div>
              </div>
              <div class="nf-fc-body">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <span class="nf-org-badge" :class="orgClass(proj.client_org_type)">
                    <i :class="orgIcon(proj.client_org_type) + ' me-1'"></i>{{ proj.org_label }}
                  </span>
                  <span class="text-muted small ms-auto" v-if="proj.year_completed">
                    <i class="fas fa-calendar-alt me-1"></i>{{ proj.year_completed }}
                  </span>
                </div>
                <h3 class="nf-fc-title">{{ proj.title }}</h3>
                <p class="text-muted small mb-3">
                  <i class="fas fa-briefcase me-1 text-primary"></i>{{ proj.client_name }}
                  <span v-if="proj.location" class="ms-3">
                    <i class="fas fa-map-marker-alt me-1 text-primary"></i>{{ proj.location }}
                  </span>
                </p>
                <p class="nf-fc-desc">{{ proj.description }}</p>
                <button class="btn btn-primary btn-sm rounded-pill px-4 mt-1">
                  View Details <i class="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══ ALL PROJECTS ═══ -->
    <section class="py-6 px-3 px-lg-5" style="background:var(--nf-light);">
      <div class="container-fluid">
        <div class="text-center mb-5">
          <span class="section-label">Full Portfolio</span>
          <h2 class="section-title">All Projects</h2>
          <hr class="section-divider" />
        </div>

        <!-- Category filter tabs -->
        <div class="nf-filter-tabs mb-5">
          <button
            class="nf-filter-btn"
            :class="{ active: activeCategory === null }"
            @click="setCategory(null)"
          >
            <i class="fas fa-th me-2"></i>All
            <span class="nf-filter-count">{{ allProjects.length }}</span>
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="nf-filter-btn"
            :class="{ active: activeCategory === cat.slug }"
            :style="activeCategory === cat.slug ? { background: cat.color, borderColor: cat.color } : {}"
            @click="setCategory(cat.slug)"
          >
            <i :class="cat.icon_class + ' me-2'"></i>{{ cat.name }}
            <span class="nf-filter-count">{{ cat.project_count }}</span>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="nf-loader"></div>
          <p class="text-muted mt-3">Loading projects…</p>
        </div>

        <!-- Empty -->
        <div v-else-if="!filteredProjects.length" class="text-center py-5">
          <i class="fas fa-folder-open fa-3x text-muted mb-3 d-block"></i>
          <p class="text-muted">No projects in this category yet.</p>
        </div>

        <!-- Grid -->
        <transition-group v-else name="nf-proj" tag="div" class="row g-4" appear>
          <div
            v-for="proj in filteredProjects"
            :key="proj.id"
            class="col-12 col-sm-6 col-xl-4"
          >
            <div class="nf-proj-card" @click="openModal(proj)">
              <!-- Image -->
              <div class="nf-pc-img">
                <img :src="proj.image_url || fallbackImg" :alt="proj.title" />
                <div class="nf-pc-img-overlay"></div>
                <!-- Category pill -->
                <div v-if="proj.category" class="nf-pc-cat-pill" :style="{background: proj.category.color}">
                  <i :class="proj.category.icon_class + ' me-1'"></i>{{ proj.category.name }}
                </div>
                <!-- Status dot -->
                <span class="nf-pc-status" :class="statusClass(proj.status)">
                  {{ proj.status_label }}
                </span>
                <!-- Featured ribbon -->
                <div v-if="proj.featured" class="nf-ribbon">Featured</div>
              </div>

              <!-- Body -->
              <div class="nf-pc-body">
                <h5 class="nf-pc-title">{{ proj.title }}</h5>

                <div class="nf-pc-meta">
                  <span><i class="fas fa-briefcase me-1"></i>{{ proj.client_name }}</span>
                  <span v-if="proj.location"><i class="fas fa-map-marker-alt me-1"></i>{{ proj.location }}</span>
                  <span v-if="proj.year_completed"><i class="fas fa-calendar-alt me-1"></i>{{ proj.year_completed }}</span>
                </div>

                <div class="nf-pc-org">
                  <i :class="orgIcon(proj.client_org_type) + ' me-1'"></i>{{ proj.org_label }}
                </div>

                <p class="nf-pc-desc">{{ proj.description }}</p>

                <div class="nf-pc-footer">
                  <button class="btn btn-sm btn-outline-primary rounded-pill px-3">
                    View Details <i class="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </section>

    <!-- ═══ PROJECT DETAIL MODAL ═══ -->
    <transition name="nf-fade">
      <div v-if="selectedProject" class="nf-modal-backdrop" @click.self="closeModal">
        <transition name="nf-modal-slide">
          <div v-if="selectedProject" class="nf-modal-box" role="dialog" aria-modal="true">
            <!-- Header image -->
            <div class="nf-modal-hero">
              <img :src="selectedProject.image_url || fallbackImg" :alt="selectedProject.title" />
              <div class="nf-modal-hero-overlay"></div>

              <button class="nf-modal-close" @click="closeModal" aria-label="Close">
                <i class="fas fa-times"></i>
              </button>

              <div class="nf-modal-hero-content">
                <div v-if="selectedProject.category" class="nf-fc-cat d-inline-flex mb-2"
                  :style="{background: selectedProject.category.color}">
                  <i :class="selectedProject.category.icon_class + ' me-1'"></i>
                  {{ selectedProject.category.name }}
                </div>
                <h2 class="text-white mb-0">{{ selectedProject.title }}</h2>
              </div>
            </div>

            <!-- Body -->
            <div class="nf-modal-body">
              <div class="row g-4">
                <!-- Left: meta -->
                <div class="col-12 col-md-4">
                  <div class="nf-modal-meta-box">
                    <div class="nf-mm-item">
                      <div class="nf-mm-icon"><i class="fas fa-briefcase"></i></div>
                      <div>
                        <div class="nf-mm-label">Client</div>
                        <div class="nf-mm-val">{{ selectedProject.client_name }}</div>
                      </div>
                    </div>
                    <div class="nf-mm-item">
                      <div class="nf-mm-icon"><i :class="orgIcon(selectedProject.client_org_type)"></i></div>
                      <div>
                        <div class="nf-mm-label">Organisation Type</div>
                        <div class="nf-mm-val">{{ selectedProject.org_label }}</div>
                      </div>
                    </div>
                    <div class="nf-mm-item" v-if="selectedProject.location">
                      <div class="nf-mm-icon"><i class="fas fa-map-marker-alt"></i></div>
                      <div>
                        <div class="nf-mm-label">Location</div>
                        <div class="nf-mm-val">
                          {{ selectedProject.location }}
                          <span v-if="selectedProject.county">, {{ selectedProject.county }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="nf-mm-item" v-if="selectedProject.year_completed">
                      <div class="nf-mm-icon"><i class="fas fa-calendar-alt"></i></div>
                      <div>
                        <div class="nf-mm-label">Year</div>
                        <div class="nf-mm-val">{{ selectedProject.year_completed }}</div>
                      </div>
                    </div>
                    <div class="nf-mm-item">
                      <div class="nf-mm-icon"><i class="fas fa-circle" :class="statusClass(selectedProject.status)" style="font-size:.65rem;"></i></div>
                      <div>
                        <div class="nf-mm-label">Status</div>
                        <div class="nf-mm-val">{{ selectedProject.status_label }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Right: content -->
                <div class="col-12 col-md-8">
                  <h5 class="nf-modal-section-head">Overview</h5>
                  <p>{{ selectedProject.description }}</p>

                  <template v-if="selectedProject.objectives">
                    <h5 class="nf-modal-section-head mt-4">Objectives</h5>
                    <p style="white-space:pre-line;">{{ selectedProject.objectives }}</p>
                  </template>

                  <template v-if="selectedProject.outcomes">
                    <h5 class="nf-modal-section-head mt-4">Outcomes &amp; Results</h5>
                    <p style="white-space:pre-line;">{{ selectedProject.outcomes }}</p>
                  </template>
                </div>
              </div>

              <div class="text-end mt-4 pt-3 border-top">
                <button class="btn btn-outline-primary rounded-pill px-4 me-2" @click="closeModal">Close</button>
                <router-link to="/quote" class="btn btn-primary rounded-pill px-4" @click="closeModal">
                  Request Similar Service
                </router-link>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>

  </div>
</template>

<script>
const BASE = 'https://admin.nexusfocusconsultancy.com';

export default {
  name: 'ProjectsView',
  data() {
    return {
      categories:      [],
      allProjects:     [],
      featured:        [],
      activeCategory:  null,
      loading:         true,
      selectedProject: null,
      fallbackImg:     '/img/carousel-1.jpeg',
      statsStrip: [
        { value: '—', label: 'Total Projects' },
        { value: '—', label: 'Categories' },
        { value: '—', label: 'Counties Reached' },
        { value: '—', label: 'Completed' },
      ],
    };
  },
  computed: {
    filteredProjects() {
      if (!this.activeCategory) return this.allProjects;
      return this.allProjects.filter(p => p.category?.slug === this.activeCategory);
    },
  },
  async mounted() {
    document.addEventListener('keydown', this.onKey);
    await this.fetchAll();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    async fetchAll() {
      this.loading = true;
      try {
        const [catRes, projRes, featRes] = await Promise.all([
          fetch(`${BASE}/projects/api/categories/`),
          fetch(`${BASE}/projects/api/projects/`),
          fetch(`${BASE}/projects/api/projects/?featured=true`),
        ]);
        this.categories  = await catRes.json();
        this.allProjects = await projRes.json();
        this.featured    = await featRes.json();
        this.buildStats();
        this.$nextTick(() => window.dispatchEvent(new Event('nf-reobserve')));
      } catch (e) {
        console.error('Failed to load projects:', e);
      } finally {
        this.loading = false;
      }
    },
    buildStats() {
      const completed = this.allProjects.filter(p => p.status === 'completed').length;
      const counties  = [...new Set(this.allProjects.map(p => p.county).filter(Boolean))].length;
      this.statsStrip = [
        { value: this.allProjects.length, label: 'Total Projects' },
        { value: this.categories.length,  label: 'Service Categories' },
        { value: counties || '—',         label: 'Counties Reached' },
        { value: completed,               label: 'Completed' },
      ];
    },
    setCategory(slug) {
      this.activeCategory = slug;
    },
    openModal(proj) {
      this.selectedProject = proj;
      document.body.style.overflow = 'hidden';
    },
    closeModal() {
      this.selectedProject = null;
      document.body.style.overflow = '';
    },
    onKey(e) {
      if (e.key === 'Escape') this.closeModal();
    },
    statusClass(s) {
      return {
        'status-completed': s === 'completed',
        'status-ongoing':   s === 'ongoing',
        'status-upcoming':  s === 'upcoming',
      };
    },
    orgIcon(type) {
      const map = {
        government: 'fas fa-landmark',
        ngo:        'fas fa-hands-helping',
        private:    'fas fa-industry',
        academic:   'fas fa-university',
        community:  'fas fa-users',
        donor:      'fas fa-hand-holding-usd',
        other:      'fas fa-building',
      };
      return map[type] || 'fas fa-building';
    },
    orgClass(type) {
      const map = {
        government: 'org-gov',
        ngo:        'org-ngo',
        private:    'org-priv',
        academic:   'org-acad',
        community:  'org-comm',
        donor:      'org-donor',
      };
      return map[type] || '';
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════
   HERO
══════════════════════════════════ */
.nf-project-hero {
  position: relative;
  min-height: 420px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  padding: 80px 20px 100px;
}
.nf-project-hero::after {
  content: '';
  position: absolute;
  bottom: -1px; left: 0; right: 0;
  height: 56px;
  background: #fff;
  clip-path: polygon(0 100%, 100% 100%, 100% 55%, 50% 0, 0 55%);
}
/* dot grid */
.nf-project-hero::before {
  content: '';
  position: absolute; inset: 0;
  background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 28px 28px;
}
.nf-ph-content { position: relative; z-index: 1; max-width: 700px; }
.nf-ph-label {
  display: inline-block;
  font-size: .75rem; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase;
  color: var(--nf-gold, #c9992d); margin-bottom: .6rem;
}
.nf-ph-title {
  font-family: 'Playfair Display', serif;
  color: #fff; font-size: clamp(2.2rem, 5vw, 3.4rem);
  font-weight: 700 !important; line-height: 1.2;
  text-shadow: 0 2px 24px rgba(0,0,0,.3);
}
.nf-ph-sub {
  color: rgba(255,255,255,.8); line-height: 1.75;
  font-size: .95rem; margin: .75rem 0 0;
}

/* ══════════════════════════════════
   STATS STRIP
══════════════════════════════════ */
.nf-proj-stats-strip {
  background: linear-gradient(90deg, var(--nf-green-dark,#0e4530), var(--nf-green,#1a6b4a));
  padding: 20px 0;
}
.nf-ps-item { padding: 14px 20px; }
.nf-ps-item.border-end { border-right: 1px solid rgba(255,255,255,.15) !important; }
.nf-ps-num {
  font-family: 'Playfair Display', serif;
  font-size: 2rem; font-weight: 700;
  color: #fff; line-height: 1;
}
.nf-ps-label { font-size: .78rem; color: rgba(255,255,255,.7); margin-top: .25rem; }

/* ══════════════════════════════════
   FEATURED CARD
══════════════════════════════════ */
.nf-featured-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--nf-border, #dce8e0);
  cursor: pointer;
  transition: transform .35s, box-shadow .35s;
  display: flex; flex-direction: column;
}
.nf-featured-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 28px 60px rgba(26,107,74,.14);
}
.nf-fc-image {
  position: relative;
  height: 240px; overflow: hidden;
}
.nf-fc-image img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform .5s ease;
}
.nf-featured-card:hover .nf-fc-image img { transform: scale(1.06); }
.nf-fc-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 60%);
}
.nf-fc-status {
  position: absolute; top: 14px; right: 14px;
  font-size: .72rem; font-weight: 700;
  padding: .28rem .75rem; border-radius: 20px;
  letter-spacing: .04em;
}
.nf-fc-cat {
  position: absolute; bottom: 14px; left: 14px;
  color: #fff; font-size: .72rem; font-weight: 700;
  padding: .28rem .8rem; border-radius: 20px;
  display: flex; align-items: center;
}
.nf-fc-body { padding: 1.75rem; flex: 1; display: flex; flex-direction: column; }
.nf-fc-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.3rem; color: var(--nf-dark,#111c22);
  margin-bottom: .5rem; line-height: 1.3;
}
.nf-fc-desc {
  color: var(--nf-body,#4a5568); font-size: .9rem;
  line-height: 1.7; flex: 1;
  display: -webkit-box; -webkit-line-clamp: 3;
  -webkit-box-orient: vertical; overflow: hidden;
}

/* ══════════════════════════════════
   FILTER TABS
══════════════════════════════════ */
.nf-filter-tabs {
  display: flex; flex-wrap: wrap; gap: .6rem;
}
.nf-filter-btn {
  display: flex; align-items: center; gap: .3rem;
  padding: .5rem 1.1rem;
  border: 2px solid var(--nf-border,#dce8e0);
  border-radius: 50px;
  background: #fff;
  font-size: .83rem; font-weight: 600;
  color: var(--nf-charcoal,#2c3e50);
  cursor: pointer;
  transition: all .25s;
  white-space: nowrap;
}
.nf-filter-btn:hover {
  border-color: var(--nf-green,#1a6b4a);
  color: var(--nf-green,#1a6b4a);
}
.nf-filter-btn.active {
  background: var(--nf-green,#1a6b4a);
  border-color: var(--nf-green,#1a6b4a);
  color: #fff;
}
.nf-filter-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  background: rgba(0,0,0,.1); font-size: .7rem; font-weight: 700;
}
.nf-filter-btn.active .nf-filter-count { background: rgba(255,255,255,.25); }

/* ══════════════════════════════════
   PROJECT CARD
══════════════════════════════════ */
.nf-proj-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid var(--nf-border,#dce8e0);
  overflow: hidden;
  cursor: pointer;
  height: 100%;
  display: flex; flex-direction: column;
  transition: transform .3s, box-shadow .3s;
}
.nf-proj-card:hover {
  transform: translateY(-7px);
  box-shadow: 0 22px 55px rgba(26,107,74,.12);
}
.nf-pc-img {
  position: relative; height: 200px; overflow: hidden;
}
.nf-pc-img img {
  width:100%; height:100%; object-fit:cover;
  transition: transform .5s ease;
}
.nf-proj-card:hover .nf-pc-img img { transform: scale(1.07); }
.nf-pc-img-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.45) 0%, transparent 55%);
}
.nf-pc-cat-pill {
  position: absolute; bottom: 10px; left: 10px;
  color: #fff; font-size: .68rem; font-weight: 700;
  padding: .22rem .65rem; border-radius: 20px;
  display: flex; align-items: center;
  letter-spacing: .04em;
}
.nf-pc-status {
  position: absolute; top: 10px; right: 10px;
  font-size: .68rem; font-weight: 700;
  padding: .22rem .65rem; border-radius: 20px;
}
.nf-ribbon {
  position: absolute; top: 14px; left: -28px;
  background: var(--nf-gold,#c9992d); color: #fff;
  font-size: .68rem; font-weight: 700;
  padding: .22rem 2.5rem;
  transform: rotate(-45deg);
  letter-spacing: .04em;
}

.nf-pc-body { padding: 1.25rem 1.4rem; flex:1; display:flex; flex-direction:column; }
.nf-pc-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.05rem; color: var(--nf-dark,#111c22);
  line-height: 1.35; margin-bottom: .6rem;
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}
.nf-pc-meta {
  display: flex; flex-wrap: wrap; gap: .5rem;
  font-size: .75rem; color: var(--nf-muted,#718096);
  margin-bottom: .5rem;
}
.nf-pc-meta i { color: var(--nf-green,#1a6b4a); }
.nf-pc-org {
  font-size: .72rem; font-weight: 600;
  margin-bottom: .75rem;
  color: var(--nf-muted,#718096);
}
.nf-pc-desc {
  font-size: .82rem; color: var(--nf-body,#4a5568);
  line-height: 1.65; flex: 1;
  display: -webkit-box; -webkit-line-clamp: 3;
  -webkit-box-orient: vertical; overflow: hidden;
  margin-bottom: .75rem;
}
.nf-pc-footer { margin-top: auto; }

/* Status colours */
.status-completed { background: rgba(26,107,74,.12); color: #1a6b4a; }
.status-ongoing   { background: rgba(201,153,45,.15); color: #a07518; }
.status-upcoming  { background: rgba(37,99,235,.12);  color: #2563eb; }

/* Org badge */
.nf-org-badge {
  font-size: .7rem; font-weight: 600;
  padding: .2rem .65rem; border-radius: 20px;
  background: var(--nf-light,#f7f9f7);
  color: var(--nf-charcoal,#2c3e50);
  border: 1px solid var(--nf-border,#dce8e0);
}

/* Card transition-group */
.nf-proj-enter-active,
.nf-proj-leave-active { transition: opacity .3s ease, transform .3s ease; }
.nf-proj-enter-from,
.nf-proj-leave-to     { opacity: 0; transform: translateY(16px); }

/* ══════════════════════════════════
   MODAL
══════════════════════════════════ */
.nf-modal-backdrop {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(17,28,34,.65);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
  overflow-y: auto;
}
.nf-fade-enter-active, .nf-fade-leave-active { transition: opacity .3s; }
.nf-fade-enter-from, .nf-fade-leave-to { opacity: 0; }

.nf-modal-box {
  background: #fff;
  border-radius: 18px;
  width: 100%; max-width: 860px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 40px 100px rgba(0,0,0,.35);
  position: relative;
}
.nf-modal-slide-enter-active, .nf-modal-slide-leave-active { transition: transform .35s ease, opacity .35s ease; }
.nf-modal-slide-enter-from, .nf-modal-slide-leave-to { transform: translateY(32px); opacity: 0; }

.nf-modal-hero {
  position: relative; height: 280px; overflow: hidden;
  border-radius: 18px 18px 0 0;
}
.nf-modal-hero img { width: 100%; height: 100%; object-fit: cover; }
.nf-modal-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.75) 0%, rgba(0,0,0,.2) 60%, transparent 100%);
}
.nf-modal-hero-content {
  position: absolute; bottom: 24px; left: 28px; right: 60px;
}
.nf-modal-close {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 36px; height: 36px; border-radius: 50%;
  background: rgba(255,255,255,.2); border: 2px solid rgba(255,255,255,.5);
  color: #fff; font-size: .9rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .2s;
}
.nf-modal-close:hover { background: rgba(255,255,255,.35); }

.nf-modal-body { padding: 2rem 2rem 2.5rem; }

/* Meta box */
.nf-modal-meta-box {
  background: var(--nf-light,#f7f9f7);
  border-radius: 12px; padding: 1.25rem;
  display: flex; flex-direction: column; gap: 1rem;
}
.nf-mm-item { display: flex; align-items: flex-start; gap: .75rem; }
.nf-mm-icon {
  width: 34px; height: 34px; flex-shrink: 0;
  border-radius: 50%; background: var(--nf-green-tint,#e7f4ed);
  display: flex; align-items: center; justify-content: center;
  font-size: .8rem; color: var(--nf-green,#1a6b4a);
}
.nf-mm-label { font-size: .7rem; text-transform: uppercase; letter-spacing: .06em; color: var(--nf-muted,#718096); }
.nf-mm-val   { font-size: .88rem; font-weight: 600; color: var(--nf-charcoal,#2c3e50); }

.nf-modal-section-head {
  font-family: 'Playfair Display', serif;
  font-size: 1.05rem; color: var(--nf-dark,#111c22);
  padding-bottom: .4rem;
  border-bottom: 2px solid var(--nf-green,#1a6b4a);
  display: inline-block; margin-bottom: .75rem;
}

/* Loader */
.nf-loader {
  width: 40px; height: 40px; border-radius: 50%;
  border: 4px solid var(--nf-green-tint,#e7f4ed);
  border-top-color: var(--nf-green,#1a6b4a);
  animation: nfSpin .8s linear infinite;
  margin: 0 auto;
}
@keyframes nfSpin { to { transform: rotate(360deg); } }

@media (max-width: 767px) {
  .nf-modal-body { padding: 1.25rem; }
  .nf-modal-hero { height: 200px; }
}
</style>
